<template lang="pug">
v-main
  v-container(fluid)
    v-card
      v-toolbar.elevation-0(color="white")
        v-toolbar-title Bulk Upload COA
        v-spacer
        v-btn.mr-2(outlined class="text-capitalize" color="blue", @click="downloadCSVTemplate")
          v-icon.mr-2 cloud_download
          | CSV Template
        v-btn(outlined class="text-capitalize" color="green", @click="downloadExcelTemplate")
          v-icon.mr-2 cloud_download
          | Excel Template
      v-divider
      v-card-text
        v-form(ref="form", v-model="valid")
          v-row(no-gutters)
            v-col(cols="3") 
              v-subheader(color="black") Business Unit
                span(style="color: #d42a2a") *
            v-col(cols="9")
              v-select(
                v-model="selectedBU",
                :items="filteredBUList",
                item-title="business_unit.bu_name",
                item-value="business_unit.id",
                label="Select Business Unit",
                :rules="[rules.required]",
                required
              )
                template(v-slot:item="data")
                  v-list-item-title {{ getBUName(data.item) }}
                template(v-slot:selection="data")
                  span {{ getBUName(data.item) }}

          v-row(no-gutters)
            v-col(cols="3") 
              v-subheader(color="black") Upload File
                span(style="color: #d42a2a") *
            v-col(cols="9")
              v-file-input(
                label="Upload File (Max Size: 5 MB. Format: csv, xls, xlsx)."
                v-model="file",
                accept=".csv,.xls,.xlsx",
                @change="handleFileUpload",
                :rules="[rules.required, rules.max]",
                hint="Columns: code, description, account_type. CSV supports comma, semicolon, or tab delimiter."
                persistent-hint
              )

          v-alert(
            v-if="csvErrors.length > 0"
            dismissible 
            color="red" 
            class="mt-5" 
            dark 
            v-model='showCsvErrors' 
            transition="scale-transition"
          ) 
            | The file you uploaded has errors. Please check the details below.

          v-toolbar(color="grey lighten-4 elevation-0 mt-5" height="48") 
            | Preview File ({{ previewData.length }} items)

          v-data-table(
            v-if="previewData.length > 0 && !csvErrors.length"
            class="mt-2",
            :headers="dataHeaders",
            :items="previewData",
          )

          v-data-table(
            v-if="csvErrors.length"
            class="mt-2",
            :headers="errorHeaders",
            :items="csvErrors",
            :item-class="row_classes"
          )

          div(v-if="!file && csvErrors.length === 0" class="text-center no-data")
            img(src="../assets/no-data.png" class="mb-1")
            h3 No Data 
            p(color="grey lighten-4") Upload file and preview

        v-card-actions.my-8
          v-btn.custom__padding-btn(@click="cancel") Cancel
          v-btn.custom__padding-btn(
            style="background-color:#006bff; color: white",
            @click="submit",
            :disabled="!formIsValid || csvErrors.length > 0 || uploading",
            :loading="uploading"
          ) Submit

  v-overlay(:value="overlay")
    v-progress-circular(indeterminate, size="64")
</template>

<script>
import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import axios from "axios";

export default {
  name: "COABulkUpload",
  data() {
    return {
      valid: false,
      uploading: false,
      overlay: false,
      showCsvErrors: false,
      selectedBU: "",
      buList: [],
      file: null,
      previewData: [],
      csvErrors: [],
      dataHeaders: [
        { text: "Code", value: "code" },
        { text: "Description", value: "description" },
        { text: "Account Type", value: "account_type" },
      ],
      errorHeaders: [
        { text: "Error Description", value: "reason" },
        { text: "Code", value: "data.code" },
        { text: "Description", value: "data.description" },
        { text: "Account Type", value: "data.account_type" },
      ],
      rules: {
        required: (value) => !!value || "This field is required.",
        max: (value) => !value || value.size < 5000000 ||
          "File size exceeds the limit ! (Maximum 5 MB)",
      },
    };
  },
  computed: {
    filteredBUList() {
      return this.buList.filter(item => {
        return item.business_unit &&
               item.business_unit.is_active === true &&
               item.is_payment_voucher === true;
      });
    },
    formIsValid() {
      return this.selectedBU && this.file && this.previewData.length > 0 && this.csvErrors.length === 0;
    },
  },
  mounted() {
    this.loadBUList();
  },
  methods: {
    loadBUList() {
      const token = localStorage.getItem("token");
      const userData = this.$store.state.dataUsers;
      const userId = userData.user || userData;
      
      if (userId) {
        axios
          .get(`${process.env.VUE_APP_API}/users/active/bu/${userId._id}`, {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then((res) => {
            this.buList = res.data.data || [];
          })
          .catch((err) => {
            this.$toasted.error("Failed to load business units", { icon: "error" });
          });
      }
    },
    handleFileUpload(event) {
      this.file = event;
      this.parseFile();
    },
    parseFile() {
      if (this.file != null) {
        this.overlay = true;
        const fileName = this.file.name.toLowerCase();
        const isExcel = fileName.endsWith('.xls') || fileName.endsWith('.xlsx');

        if (isExcel) {
          this.parseExcelFile();
        } else {
          this.parseCSVFile();
        }
      } else {
        // Clear data when file is removed
        this.previewData = [];
        this.csvErrors = [];
        this.showCsvErrors = false;
      }
    },
    parseCSVFile() {
      Papa.parse(this.file, {
        header: true,
        skipEmptyLines: true,
        delimiter: '', // Empty string enables auto-detection for comma, semicolon, tab, etc.
        complete: (results) => {
          this.overlay = false;
          this.processFileData(results.data, results.meta.fields);
        },
        error: (error) => {
          this.overlay = false;
          this.csvErrors = [{
            data: {},
            reason: 'Failed to parse CSV file: ' + error.message
          }];
          this.showCsvErrors = true;
          this.previewData = [];
        }
      });
    },
    parseExcelFile() {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { raw: false });
          
          // Get header columns
          const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
          const headers = [];
          for (let col = range.s.c; col <= range.e.c; col++) {
            const cell = worksheet[XLSX.utils.encode_cell({ r: 0, c: col })];
            headers.push(cell ? cell.v : '');
          }
          
          this.overlay = false;
          this.processFileData(jsonData, headers);
        } catch (error) {
          this.overlay = false;
          this.csvErrors = [{
            data: {},
            reason: 'Failed to parse Excel file: ' + error.message
          }];
          this.showCsvErrors = true;
          this.previewData = [];
        }
      };
      reader.onerror = () => {
        this.overlay = false;
        this.csvErrors = [{
          data: {},
          reason: 'Failed to read file'
        }];
        this.showCsvErrors = true;
        this.previewData = [];
      };
      reader.readAsArrayBuffer(this.file);
    },
    processFileData(data, fields) {
      // Validate required columns
      const requiredColumns = ['code', 'description', 'account_type'];
      const hasAllColumns = requiredColumns.every(col =>
        fields && fields.includes(col)
      );

      if (!hasAllColumns) {
        this.csvErrors = [{
          data: {},
          reason: 'File must contain columns: code, description, account_type'
        }];
        this.showCsvErrors = true;
        this.previewData = [];
        return;
      }

      // Validate data
      const errors = [];
      const validData = [];

      for (let i = 0; i < data.length; i++) {
        const row = data[i];

        if (!row.code || !row.description || !row.account_type) {
          errors.push({
            data: row,
            reason: 'Missing required field'
          });
        } else {
          validData.push({
            code: String(row.code).trim(),
            description: String(row.description).trim(),
            account_type: String(row.account_type).trim(),
          });
        }
      }

      if (errors.length > 0) {
        this.csvErrors = errors;
        this.showCsvErrors = true;
        this.previewData = [];
      } else {
        this.csvErrors = [];
        this.showCsvErrors = false;
        this.previewData = validData;
      }
    },
    submit() {
      if (!this.formIsValid) return;

      // Clear any existing toasts to avoid confusion
      this.$toasted.clear();

      this.uploading = true;
      const token = localStorage.getItem("token");

      const selectedBUObj = this.buList.find(bu => bu.business_unit.id === this.selectedBU);
      const payload = {
        bu_id: this.selectedBU,
        bu_name: selectedBUObj ? selectedBUObj.business_unit.bu_name : '',
        items: this.previewData,
      };

      axios
        .post(`${process.env.VUE_APP_API}/coa/bulk-upload`, payload, {
          headers: { Authorization: `Bearer ${token}` },
        })
        .then((res) => {
          this.$toasted.success(`Success! ${res.data.count} COA(s) created successfully.`, {
            icon: "check_circle",
          });
          this.$router.push({ name: "COA List" });
        })
        .catch((err) => {
          if (err.response && err.response.data && err.response.data.message && Array.isArray(err.response.data.message)) {
            // Calculate how many items were supposed to be uploaded vs how many failed
            const totalItems = this.previewData.length;
            const failedItems = err.response.data.message.length;
            const successItems = totalItems - failedItems;

            // Show appropriate message based on results
            if (successItems > 0) {
              this.$toasted.success(`${successItems} COA(s) created successfully.`, {
                icon: "check_circle",
                duration: 5000
              });
            } else if (failedItems === totalItems) {
              // All items failed, show error message
              this.$toasted.error(`Upload failed. All ${failedItems} item(s) have validation errors.`, {
                icon: "error",
                duration: 5000
              });
            }

            // Show errors for failed items
            this.csvErrors = err.response.data.message;
            this.showCsvErrors = true;
            this.previewData = [];
          } else {
            const errorMsg = (err.response && err.response.data && err.response.data.message) || "Failed to upload COA";
            this.$toasted.error(errorMsg, { icon: "error" });
          }
        })
        .finally(() => {
          this.uploading = false;
        });
    },
    cancel() {
      this.$router.push({ name: "COA List" });
    },
    downloadCSVTemplate() {
      const csvContent = "code,description,account_type\n1000,Revenue-Mutual Funds-Fixed Income,Revenue\n2000,Bank Interest Income,Other Income\n3000,Bank Charges,Expense";
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', 'coa_template.csv');
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    downloadExcelTemplate() {
      // Create workbook and worksheet
      const wb = XLSX.utils.book_new();
      const wsData = [
        ['code', 'description', 'account_type'],
        ['1000', 'Revenue-Mutual Funds-Fixed Income', 'Revenue'],
        ['2000', 'Bank Interest Income', 'Other Income'],
        ['3000', 'Bank Charges', 'Expense']
      ];
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      
      // Set column widths for better readability
      ws['!cols'] = [
        { wch: 15 }, // code
        { wch: 40 }, // description
        { wch: 15 }  // account_type
      ];
      
      XLSX.utils.book_append_sheet(wb, ws, 'COA Template');
      
      // Generate and download file
      XLSX.writeFile(wb, 'coa_template.xlsx');
    },
    getBUName(item) {
      return item.business_unit && item.business_unit.bu_name ? item.business_unit.bu_name : '';
    },
    row_classes(item) {
      if (item.reason != '') {
        return "custom-tr";
      }
    },
  },
};
</script>

<style scoped>
.custom__padding-btn {
  margin-right: 0.5rem;
}

.no-data {
  margin-top: 2rem;
  margin-bottom: 6rem;
}

tr.custom-tr td:first-child {
  color: red;
}
</style>
